/**
 * Business Type Field Mappings
 * 
 * Defines required fields for each business claim type based on backend requirements.
 * Evidence from DeathClaim.js and DisabilityClaim.js domain models.
 * 
 * Usage:
 * - Form validation to determine required fields per business type
 * - Dynamic field rendering based on selected business type
 * - Business rule compliance checking
 */

export const BUSINESS_TYPE_FIELD_MAPPINGS = {
  // Death Claims
  'DTH': ['deceasedName', 'deceasedDateOfDeath', 'causeOfDeath', 'relationshipToDeceased'],
  
  // Spousal Cover
  '010': ['deceasedName', 'spouseEmploymentStatus', 'marriageCertificateNumber'],
  
  // Coffin Cover
  '012': ['funeralHomeName', 'burialCosts', 'burialLocation'],
  
  // Parent Cover
  '013': ['deceasedName', 'parentRelationship', 'dependencyProof'],
  
  // Disability Claims
  'PTD': ['disabilityType', 'dateOfInjury', 'permanentDisabilityPercentage'],
  'TTD': ['disabilityType', 'dateOfInjury', 'expectedEndDate'],
  
  // Critical Illness
  '002': ['diagnosis', 'diagnosisDate', 'illnessStage'],
  
  // Medical Cover
  '005': ['medicalProvider', 'treatmentDate', 'medicalExpenseAmount']
};

/**
 * Get required fields for a specific business type
 * @param {string} businessType - Business type code (DTH, 010, 012, etc.)
 * @returns {string[]} Array of required field names
 */
export const getRequiredFieldsForBusinessType = (businessType) => {
  return BUSINESS_TYPE_FIELD_MAPPINGS[businessType] || [];
};

/**
 * Check if a field is required for a specific business type
 * @param {string} fieldName - Name of the field to check
 * @param {string} businessType - Business type code
 * @returns {boolean} True if field is required for the business type
 */
export const isFieldRequiredForBusinessType = (fieldName, businessType) => {
  return BUSINESS_TYPE_FIELD_MAPPINGS[businessType]?.includes(fieldName) || false;
};

/**
 * Get all business types that require a specific field
 * @param {string} fieldName - Name of the field
 * @returns {string[]} Array of business type codes that require this field
 */
export const getBusinessTypesRequiringField = (fieldName) => {
  return Object.keys(BUSINESS_TYPE_FIELD_MAPPINGS).filter(
    businessType => BUSINESS_TYPE_FIELD_MAPPINGS[businessType].includes(fieldName)
  );
};